import {ApiClient} from '@hconnect/apiclient'
import axios, {AxiosError} from 'axios'
import {useCallback, useEffect, useState} from 'react'

import {EquipmentIdType, EquipmentPlain} from '../types'

interface UseEquipmentOptions<T> {
  enabled?: boolean
  select?: (data: EquipmentPlain | undefined) => T
  onSuccess?: (data: T) => void
  onError?: (error: AxiosError) => void
}

export const useEquipment = <T = EquipmentPlain | undefined>(
  plantId: string,
  equipmentId: string,
  idType?: EquipmentIdType,
  language?: string,
  options?: UseEquipmentOptions<T>
) => {
  const {enabled = true, select, onSuccess, onError} = options || {}

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchEquipment = useCallback(async () => {
    if (!plantId || !equipmentId) return

    setIsLoading(true)

    try {
      const path = `/plantconfig/${plantId}/equipment`
      const response = await ApiClient.axiosInstance.get<EquipmentPlain>(path, {
        params: {
          idType,
          id: equipmentId,
          language
        }
      })

      const result = select ? select(response.data) : (response.data as T)

      setData(result)
      setError(null)
      onSuccess?.(result)
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err)
        onError?.(err)
      } else {
        console.error('Unexpected error:', err)
      }
    } finally {
      setIsLoading(false)
    }
  }, [plantId, equipmentId, idType, language, select, onSuccess, onError])

  useEffect(() => {
    if (enabled && plantId && equipmentId) {
      void fetchEquipment()
    }
  }, [enabled, fetchEquipment, plantId, equipmentId])

  return {
    data,
    error,
    isLoading,
    refetch: fetchEquipment
  }
}
